import Mention from '@tiptap/extension-mention';
import { ReactRenderer } from '@tiptap/react';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import React from 'react';
import tippy, { Instance as TippyInstance, Props as TippyProps } from 'tippy.js';

import { cn } from '@/lib/utils';
import type { GithubCollaborator } from '@/services/repo';

// ----------------------------------------------------------------------------
// Public helper --------------------------------------------------------------
// ----------------------------------------------------------------------------
/**
 * Creates a TipTap `Mention` extension instance configured to show
 * suggestions sourced from the given list of GitHub collaborators.
 * Call this inside a `useMemo` to avoid recreating on every render.
 */
export function createCollaboratorMentionExtension(getCollaborators: () => GithubCollaborator[]) {
  /**
   * Narrow down the suggestion list based on the current search query.
   */
  const getItems = (query: string) => {
    const collaborators = getCollaborators() || [];

    if (!query) {
      return collaborators.slice(0, 5);
    }

    return collaborators.filter((c) => c.login.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  };

  return Mention.configure({
    HTMLAttributes: {
      class: 'mention font-medium text-primary select-text',
    },
    renderText({ options, node }: { options: any; node: any }) {
      return `${options.suggestion.char ?? '@'}${node.attrs.label}`;
    },
    suggestion: {
      char: '@',
      startOfLine: false,
      items: ({ query }: { query: string }) => getItems(query),
      render: () => {
        let reactRenderer: ReactRenderer<any> | null = null;
        let popup: TippyInstance<TippyProps> | null = null;

        return {
          onStart: (props: SuggestionProps & any) => {
            reactRenderer = new ReactRenderer(MentionList, {
              props: {
                items: (props as any).items,
                command: (props as any).command,
                query: props.query,
                selectedIndex: (props as any).selectedIndex ?? 0,
              },
              editor: props.editor,
            });

            const [instance] = tippy('body', {
              getReferenceClientRect: props.clientRect as any,
              appendTo: () => document.body,
              content: reactRenderer.element,
              showOnCreate: true,
              interactive: true,
              trigger: 'manual',
              placement: 'bottom-start',
            }) as unknown as [TippyInstance<TippyProps>];

            popup = instance;
          },
          onUpdate(props: SuggestionProps & any) {
            reactRenderer?.updateProps({
              items: (props as any).items,
              command: (props as any).command,
              query: props.query,
              selectedIndex: (props as any).selectedIndex ?? 0,
            });
            popup?.setProps({
              getReferenceClientRect: props.clientRect as any,
            });
          },
          onKeyDown(props: SuggestionKeyDownProps) {
            if (props.event.key === 'Escape') {
              popup?.hide();
              return true;
            }
            return (reactRenderer?.ref as any)?.onKeyDown?.(props) ?? false;
          },
          onExit() {
            if (popup && !(popup.state as any).isDestroyed) {
              popup.destroy();
            }
            reactRenderer?.destroy();
          },
        };
      },
    },
  });
}

// ----------------------------------------------------------------------------
// React components -----------------------------------------------------------
// ----------------------------------------------------------------------------

interface MentionListProps {
  items: GithubCollaborator[];
  command: (props: { id: string; label: string }) => void;
  query: string;
  selectedIndex: number; // initial index provided by suggestion plugin
}

interface MentionListRef {
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

/**
 * Renders the suggestion dropdown inside a React portal via `ReactRenderer` and
 * exposes an imperative `onKeyDown` handler so the outer suggestion plugin can
 * delegate keyboard navigation.
 */
const MentionList = React.forwardRef<MentionListRef, MentionListProps>(
  ({ items, command, selectedIndex: initialIndex = 0 }: MentionListProps, ref) => {
    const [selectedIndex, setSelectedIndex] = React.useState(initialIndex);

    // Keep internal index in sync when items or initialIndex change (e.g. on search query update)
    React.useEffect(() => {
      setSelectedIndex((current) => {
        // If current index is still within range, keep it, otherwise reset
        if (current < items.length) return current;
        return 0;
      });
    }, [items]);

    React.useEffect(() => {
      setSelectedIndex(initialIndex);
    }, [initialIndex]);

    // Helper to execute the command for the currently highlighted item
    const selectItem = React.useCallback(
      (item: GithubCollaborator) => {
        command({ id: item.login, label: item.login });
      },
      [command]
    );

    const handleKeyDown = React.useCallback(
      ({ event }: SuggestionKeyDownProps): boolean => {
        if (items.length === 0) return false;

        const key = event.key;
        const isCtrl = event.ctrlKey || event.metaKey; // Support Cmd on mac as meta

        // Down navigation (ArrowDown or Ctrl+n)
        if (key === 'ArrowDown' || (isCtrl && key.toLowerCase() === 'n')) {
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }

        // Up navigation (ArrowUp or Ctrl+p)
        if (key === 'ArrowUp' || (isCtrl && key.toLowerCase() === 'p')) {
          setSelectedIndex((prev) => (prev - 1 + items.length) % items.length);
          return true;
        }

        // Confirm selection with Enter or Tab
        if (key === 'Enter' || key === 'Tab') {
          event.preventDefault();
          event.stopPropagation(); // Stop the event from reaching the comment menu handler
          selectItem(items[selectedIndex]);
          return true;
        }

        return false;
      },
      [items, selectedIndex, selectItem]
    );

    React.useImperativeHandle(ref, () => ({ onKeyDown: handleKeyDown }), [handleKeyDown]);

    if (items.length === 0) {
      return <div className="rounded-md border bg-popover px-3 py-2 text-sm text-muted-foreground">No one found</div>;
    }

    return (
      <ul className="min-w-[200px] overflow-hidden rounded-md border bg-popover py-1 text-sm shadow-md" role="listbox">
        {items.map((item, index) => (
          <li
            key={item.id ?? item.login}
            role="option"
            className={cn(
              'flex cursor-pointer items-center gap-2 px-3 py-1.5',
              index === selectedIndex && 'bg-accent text-accent-foreground'
            )}
            onMouseEnter={() => setSelectedIndex(index)}
            onClick={() => selectItem(item)}
          >
            <img src={item.avatar_url} alt={item.login} className="h-5 w-5 rounded-full" />
            <span className="truncate">{item.login}</span>
          </li>
        ))}
      </ul>
    );
  }
);
